import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatHumanName } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import { useHasPermission } from '@/hooks/useRbac';
import { useCurrentUserPractitioner } from '@/hooks/useCurrentUserPractitioner';
import type {
  Appointment,
  Condition,
  Coverage,
  Encounter,
  EpisodeOfCare,
  Group,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes';
import { fetchMedplumClientChartBundle } from '@/lib/medplumChartBundle';
import { loadEpisodeTreatmentPlanView } from '@/hooks/useChartEpisodeTreatmentPlan';
import { fetchMedplumPatientContactRows } from '@/hooks/useMedplumPatientContacts';
import { patientCompartmentSearch, searchCoveragesForPatient } from '@/lib/patientCoverageSearch';
import {
  findEncounterForAppointment,
  patientDisplayLabelFromReference,
} from '@/pages/appointments/utils/fhirHelpers';
import {
  getAppointmentGroupRef,
  isCohortGroupAppointment,
} from '@/lib/groupTherapy/groupSharedNote';
import type {
  Assessment,
  AssessmentDataPoint,
  ClinicalNote,
  Episode,
  Medication,
  TreatmentPlan,
} from '@/pages/clients/data/types';
import type { ParticipantAttendanceFhirStatus } from '@/pages/appointments/utils/participantAttendance';
import type { GroupParticipant, ProtocolSection, SessionAppointment, SessionClient } from '../data/mockSessionData';
import {
  mapAppointmentToSessionAppointment,
  mapPatientToSessionClient,
  resolveEpisodeIdFromAppointment,
} from '../lib/sessionFhirMappers';
import {
  persistSessionAttendance,
  readInitialAttendanceFromAppointment,
  type SessionAttendanceEntry,
} from '../lib/sessionAttendance';
import { getZoomSidebarSessionNotes, setZoomSidebarSessionNotes } from '../lib/zoomSidebarNotes';
import { filterZoomSidebarClinicalNotes } from '../lib/zoomClinicalNotesFilter';
import { pickEmergencyContactFromContactRows } from '../lib/sessionEmergencyContact';
import { mockProtocols, mockSessionMedications } from '../data/mockSessionData';
import { isGroupTherapySession } from '../lib/sessionEpisodeContext';
import { resolveGroupSessionHeader } from '../lib/groupSessionIdentity';
import {
  buildSessionContacts,
  initialsFromName,
  rosterPatientIdsFromAppointment,
  type SessionContactInfo,
} from '../lib/sessionContactInfo';
import { isNursePractitioner } from '../lib/sessionPrescribeAccess';
import {
  applyClientLocationConfirmationToAppointment,
  getClientLocationConfirmationForPatient,
  getClientLocationConfirmationsFromAppointment,
} from '@/lib/sessionLocation/clientLocationConfirmationFhir';
import { saveClientLocationConfirmation } from '@/lib/sessionLocation/clientLocationConfirmationService';
import type { ClientLocationConfirmation } from '@/lib/sessionLocation/types';
import { useMedplumSubscriptionRefresh } from '@/hooks/useMedplumSubscriptionRefresh';
import { buildAppointmentByIdSubscriptionCriteria } from '@/lib/fhirRealtimeCriteria';
import { useSessionAutosave, type SessionAutosaveStatus } from './useSessionAutosave';

export interface UseZoomSessionDataOptions {
  /** When false, skips chart bundle and related clinical reads (client Zoom join). */
  loadClinicalData?: boolean;
}

export interface ZoomSessionData {
  loading: boolean;
  error: string | null;
  fhirAppointment: Appointment | null;
  encounter: Encounter | null;
  episodeOfCare: EpisodeOfCare | null;
  client: SessionClient | null;
  appointment: SessionAppointment | null;
  notes: ClinicalNote[];
  /** Episode-scoped chart notes including treatment-plan documents. */
  chartNotes: ClinicalNote[];
  episodes: Episode[];
  plans: TreatmentPlan[];
  assessments: Assessment[];
  assessmentTrends: AssessmentDataPoint[];
  protocols: ProtocolSection[];
  medications: Medication[];
  patientId: string | undefined;
  episodeId: string | undefined;
  sidebarNotes: string;
  setSidebarNotes: (text: string) => void;
  sidebarNotesStatus: SessionAutosaveStatus;
  sidebarNotesError: string | null;
  flushSidebarNotes: () => Promise<void>;
  retrySidebarNotes: () => Promise<void>;
  saveAttendance: (entries: SessionAttendanceEntry[]) => Promise<void>;
  refreshChart: () => Promise<void>;
  initialAttendance: Map<string, ParticipantAttendanceFhirStatus>;
  contacts: SessionContactInfo[];
  isGroupTherapyEpisode: boolean;
  groupId: string | undefined;
  groupName: string | undefined;
  groupInitials: string | undefined;
  groupSubtitle: string | undefined;
  showPrescribeTab: boolean;
  clientLocationConfirmations: Record<string, ClientLocationConfirmation>;
  saveClientLocation: (
    patientId: string,
    values: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      notAtUsualAddress: boolean;
      attestationAccepted: boolean;
    },
  ) => Promise<void>;
  clientLocationSaving: boolean;
  clientLocationSaveError: string | null;
}

function filterByEpisode<T extends { episodeId?: string }>(items: T[], episodeId: string | undefined): T[] {
  if (!episodeId) return items;
  return items.filter((item) => !item.episodeId || item.episodeId === episodeId);
}

export function useZoomSessionData(
  appointmentId: string | undefined,
  options: UseZoomSessionDataOptions = {},
): ZoomSessionData {
  const loadClinicalData = options.loadClinicalData !== false;
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fhirAppointment, setFhirAppointment] = useState<Appointment | null>(null);
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [episodeOfCare, setEpisodeOfCare] = useState<EpisodeOfCare | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [chartNotes, setChartNotes] = useState<ClinicalNote[]>([]);
  const [chartAssessments, setChartAssessments] = useState<Assessment[]>([]);
  const [chartTrends, setChartTrends] = useState<AssessmentDataPoint[]>([]);
  const [chartPlans, setChartPlans] = useState<TreatmentPlan[]>([]);
  const [chartEpisodes, setChartEpisodes] = useState<Episode[]>([]);
  const [emergencyContact, setEmergencyContact] = useState<SessionClient['emergencyContact']>();
  const [participantPatients, setParticipantPatients] = useState<Patient[]>([]);
  const [cohortGroup, setCohortGroup] = useState<Group | null>(null);
  const [groupRefId, setGroupRefId] = useState<string | undefined>();
  const [groupRefDisplay, setGroupRefDisplay] = useState<string | undefined>();
  const [sidebarNotes, setSidebarNotesState] = useState('');
  const [notesBaselineEpoch, setNotesBaselineEpoch] = useState(0);
  const [clientLocationSaving, setClientLocationSaving] = useState(false);
  const fhirAppointmentRef = useRef(fhirAppointment);
  useEffect(() => {
    fhirAppointmentRef.current = fhirAppointment;
  }, [fhirAppointment]);
  const appointmentWriteChainRef = useRef(Promise.resolve());

  const runSerializedAppointmentWrite = useCallback(
    async (writer: (appt: Appointment) => Promise<Appointment>): Promise<Appointment | undefined> => {
      let captured: Appointment | undefined;
      const turn = appointmentWriteChainRef.current.then(async () => {
        const appt = fhirAppointmentRef.current;
        if (!appt?.id) return;
        const saved = await writer(appt);
        fhirAppointmentRef.current = saved;
        setFhirAppointment(saved);
        captured = saved;
      });
      appointmentWriteChainRef.current = turn.then(
        () => undefined,
        () => undefined,
      );
      await turn;
      return captured;
    },
    [],
  );
  const [clientLocationSaveError, setClientLocationSaveError] = useState<string | null>(null);
  const { practitioner: currentPractitioner } = useCurrentUserPractitioner();
  const canUseErx = useHasPermission('can_see_option_to_use_erx_dosespot');

  const patientId = useMemo(() => {
    const ref = (fhirAppointment?.participant ?? []).find((p) =>
      p.actor?.reference?.startsWith('Patient/'),
    )?.actor?.reference;
    return ref?.replace('Patient/', '');
  }, [fhirAppointment]);

  const episodeId = useMemo(
    () => (fhirAppointment ? resolveEpisodeIdFromAppointment(fhirAppointment) : undefined),
    [fhirAppointment],
  );

  const rosterPatientIds = useMemo(
    () => rosterPatientIdsFromAppointment(fhirAppointment),
    [fhirAppointment],
  );

  const loadChart = useCallback(
    async (pid: string, eocId: string | undefined) => {
      if (!loadClinicalData) return;
      const bundle = await fetchMedplumClientChartBundle(medplum, pid);
      setChartNotes(filterByEpisode(bundle.clinicalNotes, eocId));
      setChartAssessments(filterByEpisode(bundle.assessments, eocId));
      setChartTrends(bundle.assessmentTrends);
      const episodes = eocId
        ? bundle.episodes.filter((e) => e.id === eocId)
        : bundle.episodes;
      setChartEpisodes(episodes);
      const loadedPlans = [];
      for (const episode of episodes) {
        try {
          const plan = await loadEpisodeTreatmentPlanView(medplum, pid, episode, bundle.clinicalNotes);
          if (plan) loadedPlans.push(plan);
        } catch {
          // CarePlan is the source of truth; skip episodes that cannot be read.
        }
      }
      setChartPlans(loadedPlans);
    },
    [medplum, loadClinicalData],
  );

  const loadCore = useCallback(async () => {
    if (!appointmentId) {
      setLoading(false);
      setError('No appointment id');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const appt = await medplum.readResource('Appointment', appointmentId);
      setFhirAppointment(appt);
      if (loadClinicalData) {
        setSidebarNotesState(getZoomSidebarSessionNotes(appt));
        setNotesBaselineEpoch((n) => n + 1);
      }

      const pid = (appt.participant ?? [])
        .find((p) => p.actor?.reference?.startsWith('Patient/'))
        ?.actor?.reference?.replace('Patient/', '');
      if (!pid) {
        throw new Error('Appointment has no patient participant');
      }

      if (!loadClinicalData) {
        setLoading(false);
        return;
      }

      const eocId = resolveEpisodeIdFromAppointment(appt);
      const [pt, conds, covs, enc, eoc, contactRows] = await Promise.all([
        medplum.readResource('Patient', pid),
        medplum
          .searchResources('Condition', {
            ...patientCompartmentSearch(pid),
            'clinical-status': 'active',
            _count: '25',
          })
          .catch(() => [] as Condition[]),
        searchCoveragesForPatient(medplum, pid),
        findEncounterForAppointment(medplum, appointmentId, pid, appt.start),
        eocId ? medplum.readResource('EpisodeOfCare', eocId).catch(() => undefined) : Promise.resolve(undefined),
        fetchMedplumPatientContactRows(medplum, pid).catch(() => []),
      ]);

      setPatient(pt);
      setConditions(conds);
      setCoverages(covs);
      setEncounter(enc ?? null);
      setEpisodeOfCare(eoc ?? null);
      setEmergencyContact(pickEmergencyContactFromContactRows(contactRows));

      if (isCohortGroupAppointment(appt)) {
        const groupRef = getAppointmentGroupRef(appt);
        const resolvedGroupId = groupRef?.reference?.replace(/^Group\//, '') || undefined;
        setGroupRefId(resolvedGroupId);
        setGroupRefDisplay(groupRef?.display?.trim() || undefined);
        if (resolvedGroupId) {
          const group = await medplum.readResource('Group', resolvedGroupId).catch(() => undefined);
          setCohortGroup(group ?? null);
        } else {
          setCohortGroup(null);
        }
      } else {
        setCohortGroup(null);
        setGroupRefId(undefined);
        setGroupRefDisplay(undefined);
      }

      const provRef = (appt.participant ?? []).find((p) =>
        p.actor?.reference?.startsWith('Practitioner/'),
      )?.actor?.reference;
      if (provRef) {
        const provId = provRef.replace('Practitioner/', '');
        const prov = await medplum.readResource('Practitioner', provId).catch(() => undefined);
        setPractitioner(prov ?? null);
      } else {
        setPractitioner(null);
      }

      await loadChart(pid, eocId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, medplum, loadChart, loadClinicalData]);

  useEffect(() => {
    void loadCore();
  }, [loadCore]);

  // The roster can change while the sidebar is open (a client enrolled mid-session arrives
  // over the Appointment subscription), so fetch participants the hook has not loaded yet.
  useEffect(() => {
    if (!loadClinicalData) return undefined;
    const missing = rosterPatientIds.filter(
      (id) => !participantPatients.some((p) => p.id === id),
    );
    if (missing.length === 0) return undefined;

    let cancelled = false;
    void Promise.all(
      missing.map((id) => medplum.readResource('Patient', id).catch(() => undefined)),
    ).then((loaded) => {
      const found = loaded.filter((p) => p !== undefined);
      if (cancelled || found.length === 0) return;
      setParticipantPatients((prev) => [
        ...prev,
        ...found.filter((p) => !prev.some((existing) => existing.id === p.id)),
      ]);
    });
    return () => {
      cancelled = true;
    };
  }, [loadClinicalData, medplum, rosterPatientIds, participantPatients]);

  const subscriptionCriteria = useMemo(
    () => (appointmentId ? buildAppointmentByIdSubscriptionCriteria(appointmentId) : undefined),
    [appointmentId],
  );

  useMedplumSubscriptionRefresh(
    subscriptionCriteria,
    () => {
      if (!appointmentId) return;
      void medplum.readResource('Appointment', appointmentId).then(setFhirAppointment).catch(() => undefined);
    },
    { debugLabel: '[ZoomSession][WS]' },
  );

  const client = useMemo(() => {
    if (!patient) return null;
    const plan = chartPlans[0];
    return mapPatientToSessionClient(
      patient,
      conditions,
      coverages,
      episodeOfCare,
      practitioner ?? undefined,
      plan,
      emergencyContact,
    );
  }, [patient, conditions, coverages, episodeOfCare, practitioner, chartPlans, emergencyContact]);

  const initialAttendance = useMemo(() => {
    if (!fhirAppointment) return new Map<string, ParticipantAttendanceFhirStatus>();
    return readInitialAttendanceFromAppointment(fhirAppointment);
  }, [fhirAppointment]);

  const appointment = useMemo(() => {
    if (!fhirAppointment || !client) return null;
    const patientParticipants = (fhirAppointment.participant ?? []).filter((p) =>
      p.actor?.reference?.startsWith('Patient/'),
    );
    const loadedNameById = new Map(
      participantPatients
        .filter((p) => p.id)
        .map((p) => [p.id!, formatHumanName(p.name?.[0]) || ''] as const),
    );
    const roster: GroupParticipant[] | undefined =
      patientParticipants.length > 1
        ? patientParticipants.map((p) => {
            const id = p.actor!.reference!.replace('Patient/', '');
            const name = patientDisplayLabelFromReference(
              p.actor?.reference,
              loadedNameById.get(id) || p.actor?.display,
            );
            return {
              id,
              name,
              initials: initialsFromName(name),
              status: initialAttendance.get(id) ?? null,
            };
          })
        : undefined;
    return mapAppointmentToSessionAppointment(fhirAppointment, client, roster);
  }, [fhirAppointment, client, initialAttendance, participantPatients]);

  const setSidebarNotes = useCallback((text: string) => {
    setSidebarNotesState(text);
  }, []);

  const persistSidebarNotes = useCallback(async (text: string) => {
    await runSerializedAppointmentWrite(async (appt) => {
      const updated = setZoomSidebarSessionNotes(appt, text);
      return medplum.updateResource(updated);
    });
  }, [medplum, runSerializedAppointmentWrite]);

  const sidebarNotesAutosave = useSessionAutosave({
    value: sidebarNotes,
    persist: persistSidebarNotes,
    debounceMs: 850,
    enabled: loadClinicalData && Boolean(fhirAppointment?.id),
    contextKey: `${appointmentId ?? ''}:${notesBaselineEpoch}`,
  });

  const saveAttendance = useCallback(
    async (entries: SessionAttendanceEntry[]) => {
      await runSerializedAppointmentWrite((appt) =>
        persistSessionAttendance(medplum, appt, entries),
      );
    },
    [medplum, runSerializedAppointmentWrite],
  );

  const refreshChart = useCallback(async () => {
    if (!patientId) return;
    await loadChart(patientId, episodeId);
  }, [patientId, episodeId, loadChart]);

  const contacts = useMemo(
    (): SessionContactInfo[] =>
      buildSessionContacts({
        appointment: fhirAppointment,
        loadedPatients: patient ? [patient, ...participantPatients] : participantPatients,
        primaryPatientId: patientId,
      }),
    [patientId, fhirAppointment, participantPatients, patient],
  );

  const groupTherapyEpisode = isGroupTherapySession(fhirAppointment, episodeOfCare);
  const groupHeader = useMemo(() => {
    if (!groupTherapyEpisode) return undefined;
    const header = resolveGroupSessionHeader(cohortGroup, {
      fallbackDisplay: groupRefDisplay,
    });
    return {
      ...header,
      groupId: header.groupId ?? groupRefId,
    };
  }, [groupTherapyEpisode, cohortGroup, groupRefDisplay, groupRefId]);
  const showPrescribeTab = canUseErx || isNursePractitioner(currentPractitioner);

  const clientLocationConfirmations = useMemo(() => {
    const map: Record<string, ClientLocationConfirmation> = {};
    for (const confirmation of getClientLocationConfirmationsFromAppointment(fhirAppointment ?? undefined)) {
      map[confirmation.confirmedByPatientId] = confirmation;
    }
    return map;
  }, [fhirAppointment]);

  const saveClientLocation = useCallback(
    async (
      forPatientId: string,
      values: {
        street: string;
        city: string;
        state: string;
        postalCode: string;
        notAtUsualAddress: boolean;
        attestationAccepted: boolean;
      },
    ) => {
      if (!forPatientId) return;
      setClientLocationSaving(true);
      setClientLocationSaveError(null);
      try {
        await runSerializedAppointmentWrite(async (appt) => {
          const existing = getClientLocationConfirmationForPatient(appt, forPatientId);
          const result = await saveClientLocationConfirmation(medplum, {
            appointment: appt,
            patientId: forPatientId,
            force: true,
            input: {
              street: values.street,
              city: values.city,
              state: values.state,
              postalCode: values.postalCode,
              notAtUsualAddress: values.notAtUsualAddress,
              attestationAccepted: true,
              prefillSource: 'none',
              wasEditedAfterPrefill: true,
              ipDerivedCity: existing?.ipDerivedCity,
              ipDerivedState: existing?.ipDerivedState,
            },
          });
          return (
            result.appointment
            ?? applyClientLocationConfirmationToAppointment(appt, result.confirmation)
          );
        });
      } catch {
        setClientLocationSaveError('Could not save client location. Please try again.');
      } finally {
        setClientLocationSaving(false);
      }
    },
    [medplum, runSerializedAppointmentWrite],
  );

  return {
    loading,
    error,
    fhirAppointment,
    encounter,
    episodeOfCare,
    client,
    appointment,
    notes: filterZoomSidebarClinicalNotes(chartNotes, { excludeAppointmentId: appointmentId }),
    /** Episode-scoped chart notes (includes treatment-plan documents) for chart TP loading. */
    chartNotes,
    episodes: chartEpisodes,
    plans: chartPlans,
    assessments: chartAssessments,
    assessmentTrends: chartTrends,
    protocols: mockProtocols,
    medications: mockSessionMedications,
    patientId,
    episodeId,
    sidebarNotes,
    setSidebarNotes,
    sidebarNotesStatus: sidebarNotesAutosave.status,
    sidebarNotesError: sidebarNotesAutosave.error,
    flushSidebarNotes: sidebarNotesAutosave.flush,
    retrySidebarNotes: sidebarNotesAutosave.retry,
    saveAttendance,
    refreshChart,
    initialAttendance,
    contacts,
    isGroupTherapyEpisode: groupTherapyEpisode,
    groupId: groupHeader?.groupId,
    groupName: groupHeader?.groupName,
    groupInitials: groupHeader?.groupInitials,
    groupSubtitle: groupHeader?.groupSubtitle,
    showPrescribeTab,
    clientLocationConfirmations,
    saveClientLocation,
    clientLocationSaving,
    clientLocationSaveError,
  };
}
